<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDealDateRange } from '@/utils/poolaReportFormat.js';

const props = defineProps({
  dealTitle: { type: String, default: null },
  // { startAt, endAt } — ISO date strings or null (see useSupplierDealReport).
  dateRange: {
    type: Object,
    default: () => ({ startAt: null, endAt: null }),
  },
});

const { t, locale } = useI18n({ useScope: 'global' });

const formattedDateRange = computed(() =>
  formatDealDateRange(props.dateRange?.startAt, props.dateRange?.endAt, locale.value),
);
</script>

<template>
  <div class="report-header" data-report-section="header">
    <div class="report-header__bar">
      <span class="report-header__deal-chip">{{ dealTitle }}</span>
      <span class="report-header__closed-badge">
        <span class="report-header__dot" aria-hidden="true" />
        {{ t('poola.report.closedBadge') }}
      </span>
    </div>
    <h1 class="report-header__title">{{ t('poola.report.header.title') }}</h1>
    <p class="report-header__subtitle">
      {{ t('poola.report.header.subtitle', { deal: dealTitle, dateRange: formattedDateRange }) }}
    </p>
  </div>
</template>

<style scoped>
.report-header {
  display: flex;
  flex-direction: column;
  gap: var(--poolit-spacing-sm, 8px);
  padding-inline: var(--poolit-spacing-md, 16px);
}

.report-header__bar {
  display: flex;
  align-items: center;
  gap: var(--poolit-spacing-sm, 8px);
  flex-wrap: wrap;
}

.report-header__deal-chip {
  display: inline-flex;
  align-items: center;
  background: var(--report-surface);
  border: 1px solid var(--report-hairline);
  border-radius: var(--poolit-radius-full, 9999px);
  padding: 6px 12px;
  font-size: 13px;
  font-weight: var(--poolit-font-weight-medium, 500);
  color: var(--report-text);
}

.report-header__closed-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--report-surface-2);
  color: var(--report-text-soft);
  border-radius: var(--poolit-radius-full, 9999px);
  padding: 6px 11px;
  font-size: 12px;
  font-weight: var(--poolit-font-weight-medium, 500);
}

.report-header__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--report-text-mute);
}

.report-header__title {
  margin: 4px 0 0;
  font-size: 1.5rem;
  font-weight: var(--poolit-font-weight-bold, 700);
  color: var(--report-text);
}

.report-header__subtitle {
  margin: 0;
  color: var(--report-text-soft);
  font-size: 13.5px;
}
</style>
